import { TASK_STATUS_LABELS, type TaskSource, type TaskStatus } from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { altlastenQuery, verwaisteQuery, wartendeAbnahmenQuery } from '../../lib/queries'
import { trpc } from '../../lib/trpc'
import { AufgabenTitel } from '../AufgabeKontext'
import { Card, CardPad, SourceBadge } from '../ui/primitives'
import { Zuweisen } from '../Zuweisen'

/**
 * Reihenfolge der Gruppen: vorn, was jemand hätte anfangen sollen; hinten, was bewusst
 * geparkt ist – das wird nur durchgesehen, nicht abgearbeitet.
 */
const REIHENFOLGE: TaskStatus[] = [
  'in_arbeit',
  'review',
  'warten',
  'bereit',
  'eingang',
  'zurueckgestellt',
]

/**
 * **Reiter „Aufräumen"** ([E-358](../../../../../docs/ENTSCHEIDUNGEN.md#e-358)).
 *
 * Die alten „Hängenbleiber" waren fast nur das hier: Aufgaben mit 400 bis 900 Tagen, jeden
 * Morgen an derselben Stelle. Sie sind kein Daily-Thema, sondern eine Aufräumrunde – und
 * getrennt verfälschen sie „Heute" nicht mehr. Nils: *„90 Tage ohne Bewegung."*
 *
 * Die Sammelaktion ist dieselbe wie in „Alle Aufgaben" (`tasks.massenStatus`): dieselben
 * Regeln (was ein anderer abnehmen muss, wird übersprungen und gezählt) und dasselbe
 * Zurücknehmen.
 */
export function LeitstandAufraeumen() {
  const queryClient = useQueryClient()
  const { data: altlasten } = useQuery(altlastenQuery)
  const { data: verwaist = [] } = useQuery(verwaisteQuery)
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set())
  const [bericht, setBericht] = useState<{ text: string; aktionId?: string } | null>(null)
  const [zuweisenFuer, setZuweisenFuer] = useState<{ id: string; title: string } | null>(null)

  const auffrischen = async () => {
    await queryClient.invalidateQueries({ queryKey: ['analytics'] })
    await queryClient.invalidateQueries({ queryKey: ['tasks'] })
  }

  const setzen = useMutation({
    mutationFn: (status: 'erledigt' | 'zurueckgestellt') =>
      trpc.tasks.massenStatus.mutate({ taskIds: [...auswahl], status }),
    onSuccess: async (b, status) => {
      setBericht({
        text:
          `${b.geaendert} ${status === 'erledigt' ? 'erledigt' : 'zurückgestellt'}.` +
          (b.uebersprungen
            ? ` ${b.uebersprungen} übersprungen (Abnahme durch jemand anderen).`
            : ''),
        ...(b.aktionId ? { aktionId: b.aktionId } : {}),
      })
      setAuswahl(new Set())
      await auffrischen()
    },
  })

  const zurueck = useMutation({
    mutationFn: (id: string) => trpc.eingang.massenaktionZurueck.mutate({ id }),
    onSuccess: async (b) => {
      setBericht({
        text: b.ok ? `${b.zurueckgesetzt} zurückgenommen.` : (b.grund ?? 'Ging nicht.'),
      })
      await auffrischen()
    },
  })

  const fragen = (status: 'erledigt' | 'zurueckgestellt') => {
    const wort = status === 'erledigt' ? 'als erledigt schließen' : 'zurückstellen'
    if (window.confirm(`Diese ${auswahl.size} Aufgaben ${wort}? Lässt sich danach zurücknehmen.`)) {
      setzen.mutate(status)
    }
  }

  const umschalten = (ids: string[], an: boolean) =>
    setAuswahl((alt) => {
      const neu = new Set(alt)
      for (const id of ids) {
        if (an) neu.add(id)
        else neu.delete(id)
      }
      return neu
    })

  const gruppen = altlasten
    ? [
        ...REIHENFOLGE,
        ...new Set(altlasten.map((a) => a.status).filter((s) => !REIHENFOLGE.includes(s))),
      ]
        .map((status) => ({ status, zeilen: altlasten.filter((a) => a.status === status) }))
        .filter((g) => g.zeilen.length > 0)
    : []

  return (
    <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
      <Card>
        <CardPad>
          <div className="flex flex-wrap items-baseline gap-2">
            <b className="text-[13.5px]">Altlasten</b>
            <span className="text-xs text-muted">
              offen, seit über 90 Tagen ohne Bewegung
              {altlasten ? ` · ${altlasten.length} Aufgaben` : ''}
            </span>
          </div>

          {auswahl.size > 0 && (
            <div className="sticky unter-leiste z-10 mt-2.5 flex flex-wrap items-center gap-2 rounded-lg bg-accent-soft px-2.5 py-2 text-[12.5px] text-accent-deep">
              <b>{auswahl.size} ausgewählt</b>
              <button
                type="button"
                disabled={setzen.isPending}
                onClick={() => fragen('erledigt')}
                className="cursor-pointer rounded-lg border border-line2 bg-surface px-2.5 py-1 text-xs font-semibold text-ink hover:bg-[#f3f2ef]"
              >
                als erledigt schließen
              </button>
              <button
                type="button"
                disabled={setzen.isPending}
                onClick={() => fragen('zurueckgestellt')}
                className="cursor-pointer rounded-lg border border-line2 bg-surface px-2.5 py-1 text-xs font-semibold text-ink hover:bg-[#f3f2ef]"
              >
                zurückstellen
              </button>
              <button
                type="button"
                onClick={() => setAuswahl(new Set())}
                className="ml-auto cursor-pointer text-xs font-semibold hover:underline"
              >
                Auswahl leeren
              </button>
            </div>
          )}

          {bericht && (
            <p role="status" className="mt-2.5 text-[12.5px] text-ink2">
              {bericht.text}{' '}
              {bericht.aktionId && (
                <button
                  type="button"
                  onClick={() => zurueck.mutate(bericht.aktionId!)}
                  className="cursor-pointer font-semibold text-accent-deep hover:underline"
                >
                  Zurücknehmen
                </button>
              )}
            </p>
          )}

          {!altlasten ? (
            <div className="py-2 text-[13px] text-ink2">Lädt …</div>
          ) : altlasten.length === 0 ? (
            <p className="mt-3 text-[13px] text-ink2">Nichts liegt länger als 90 Tage. ✓</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="mt-2 w-full min-w-[560px] border-collapse text-[12.5px]">
                <thead>
                  <tr className="border-b border-line2 text-[10.5px] text-ink2">
                    <th className="w-6 px-1.5 py-1" />
                    <th className="px-1.5 py-1 text-left font-semibold">Aufgabe</th>
                    <th className="px-1.5 py-1 text-left font-semibold">Person</th>
                    <th className="px-1.5 py-1 text-right font-semibold">ohne Bewegung</th>
                    <th className="px-1.5 py-1 text-left font-semibold">Quelle</th>
                  </tr>
                </thead>
                {gruppen.map((g) => {
                  const ids = g.zeilen.map((z) => z.id)
                  const alleDrin = ids.every((id) => auswahl.has(id))
                  return (
                    <tbody key={g.status}>
                      <tr className="bg-[#fbfaf8]">
                        <td className="px-1.5 pt-2.5 pb-1">
                          <input
                            type="checkbox"
                            checked={alleDrin}
                            onChange={(e) => umschalten(ids, e.target.checked)}
                            aria-label={`Alle in „${TASK_STATUS_LABELS[g.status] ?? g.status}" auswählen`}
                            className="accent-accent"
                          />
                        </td>
                        <td
                          colSpan={4}
                          className="px-1.5 pt-2.5 pb-1 text-[11px] font-bold text-ink2"
                        >
                          {TASK_STATUS_LABELS[g.status] ?? g.status} · {g.zeilen.length}
                          {g.status === 'zurueckgestellt' && (
                            <span className="font-normal text-muted">
                              {' '}
                              (bewusst geparkt – nur zur Durchsicht)
                            </span>
                          )}
                          {g.status === 'in_arbeit' && (
                            <span className="font-normal text-muted">
                              {' '}
                              (belegen weiter einen WIP-Platz)
                            </span>
                          )}
                        </td>
                      </tr>
                      {g.zeilen.map((z) => (
                        <tr key={z.id} className="border-b border-line">
                          <td className="px-1.5 py-1.5">
                            <input
                              type="checkbox"
                              checked={auswahl.has(z.id)}
                              onChange={(e) => umschalten([z.id], e.target.checked)}
                              aria-label={`${z.titel} auswählen`}
                              className="accent-accent"
                            />
                          </td>
                          <td className="w-full max-w-0 px-1.5 py-1.5">
                            <AufgabenTitel
                              aufgabeId={z.id}
                              titel={z.titel}
                              kuerzen
                              className="block truncate"
                            />
                          </td>
                          <td className="px-1.5 py-1.5 whitespace-nowrap text-ink2">
                            {z.personName ?? <span className="text-muted">–</span>}
                          </td>
                          <td className="tabular px-1.5 py-1.5 text-right whitespace-nowrap">
                            {z.tageOhneBewegung} T
                          </td>
                          <td className="px-1.5 py-1.5">
                            <SourceBadge source={z.quelle as TaskSource} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  )
                })}
              </table>
            </div>
          )}
        </CardPad>
      </Card>

      <WartendeAbnahmen />

      <Card>
        <CardPad>
          <b className="text-[13.5px]">Liegen gebliebene Zuordnungen</b>
          <p className="mt-1 text-[12.5px] text-ink2">
            Offene Aufgaben von deaktivierten Personen. Die Zuordnung bleibt, damit der Verlauf
            lesbar ist – in der Auslastung zählen sie nicht mehr.
          </p>
          {verwaist.length === 0 ? (
            <p className="mt-2.5 text-[13px] text-muted">Gerade keine. ✓</p>
          ) : (
            verwaist.map((person) => (
              <div key={person.personId} className="mt-3">
                <div className="text-[12px] font-semibold">
                  {person.name}
                  <span className="font-normal text-muted">
                    {' · '}
                    {person.aufgaben.length === 1
                      ? '1 Aufgabe'
                      : `${person.aufgaben.length} Aufgaben`}
                    {person.inArbeit > 0 && `, ${person.inArbeit} in Arbeit`}
                  </span>
                </div>
                {person.aufgaben.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-2 border-b border-line py-1.5 text-[12.5px] last:border-b-0"
                  >
                    <AufgabenTitel
                      aufgabeId={a.id}
                      titel={a.title}
                      kuerzen
                      className="min-w-0 flex-1"
                    />
                    <span className="shrink-0 text-[11px] text-muted">
                      {TASK_STATUS_LABELS[a.status as TaskStatus] ?? a.status} · {a.alterTage} T
                    </span>
                    <button
                      type="button"
                      onClick={() => setZuweisenFuer({ id: a.id, title: a.title })}
                      aria-label={`${a.title} zuweisen`}
                      className="shrink-0 cursor-pointer rounded-md px-1.5 py-0.5 text-[11px] font-semibold text-accent-deep hover:bg-accent-soft"
                    >
                      zuweisen
                    </button>
                  </div>
                ))}
              </div>
            ))
          )}
        </CardPad>
      </Card>

      {zuweisenFuer && (
        <Zuweisen
          aufgabeId={zuweisenFuer.id}
          titel={zuweisenFuer.title}
          schliessen={() => setZuweisenFuer(null)}
        />
      )}
    </div>
  )
}

/**
 * **Was auf eine Abnahme wartet** (24.09.2026, von Nils beauftragt: *„bitte die Abnahme, mit den
 * Review Gründen und so weiter auch in «Aufräumen» im Leitstand anzeigen mit einem Knopf «An
 * Abnahme erinnern»"*).
 *
 * Anlass war sein Verdacht, *„dass viele Leute die Aufgaben nicht abnehmen"*. Nachgemessen am
 * selben Tag: drei Aufgaben auf Review, zwei davon **seit 13 Tagen** – und eine **ohne
 * eingetragenen Abnehmer**. Die ist der stille Fall: Für sie geht nie eine Erinnerung raus, und
 * niemandem fällt es auf.
 *
 * Deshalb zeigt die Liste nicht nur „wartet seit", sondern **auf wen** – und sagt es, wenn das
 * niemand ist oder wenn die Person keine Mailadresse hat. Eine Erinnerung, die ins Leere geht,
 * ist schlimmer als keine: Sie beruhigt den, der sie ausgelöst hat.
 */
function WartendeAbnahmen() {
  const queryClient = useQueryClient()
  const { data: abnahmen = [] } = useQuery(wartendeAbnahmenQuery)
  const [meldung, setMeldung] = useState<{ id: string; text: string; gut: boolean } | null>(null)

  const erinnern = useMutation({
    mutationFn: (taskId: string) => trpc.tasks.abnahmeErinnern.mutate({ taskId }),
    onSuccess: async (antwort, taskId) => {
      setMeldung({ id: taskId, text: `Erinnerung an ${antwort.an} eingestellt.`, gut: true })
      await queryClient.invalidateQueries({ queryKey: ['tasks', 'abnahmen'] })
    },
    /** **Der Grund gehört an die Zeile**, nicht in die Konsole – siehe Leitplanke 1. */
    onError: (fehler: Error, taskId) =>
      setMeldung({ id: taskId, text: fehler.message, gut: false }),
  })

  return (
    <Card>
      <CardPad>
        <div className="flex flex-wrap items-baseline gap-2">
          <b className="text-[13.5px]">Wartet auf Abnahme</b>
          <span className="text-xs text-muted">
            {abnahmen.length === 0
              ? 'nichts offen'
              : `${abnahmen.length} ${abnahmen.length === 1 ? 'Aufgabe' : 'Aufgaben'}`}
          </span>
        </div>

        {abnahmen.length === 0 ? (
          <p className="mt-2 text-[12.5px] text-muted">
            Zurzeit wartet keine Aufgabe auf jemanden.
          </p>
        ) : (
          <ul className="mt-2.5 flex flex-col gap-2">
            {abnahmen.map((a) => (
              <li key={a.id} className="rounded-lg border border-line px-2.5 py-2">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <SourceBadge source={a.sourceId as TaskSource} label={a.externalKey} />
                  <AufgabenTitel
                    aufgabeId={a.id}
                    titel={a.title}
                    className="text-[12.5px] font-semibold"
                  />
                  {a.wartetTage !== null && (
                    <span
                      className={`text-[11.5px] ${a.wartetTage >= 7 ? 'font-semibold text-pool-sofort' : 'text-muted'}`}
                    >
                      wartet {a.wartetTage} {a.wartetTage === 1 ? 'Tag' : 'Tage'}
                    </span>
                  )}
                </div>

                {/**
                 * **Der Review-Grund.** Ohne ihn ist die Zeile eine Aufforderung ohne Anlass –
                 * man müsste die Aufgabe erst öffnen, um zu erfahren, worum es geht.
                 */}
                {a.grund && <p className="mt-1 text-[12px] text-ink2 italic">„{a.grund}"</p>}

                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11.5px]">
                  {a.abnehmerName ? (
                    <span className="text-ink2">
                      nimmt ab: <b className="font-semibold">{a.abnehmerName}</b>
                      {!a.abnehmerMail && (
                        <span className="text-pool-sofort"> · keine Mailadresse</span>
                      )}
                    </span>
                  ) : (
                    <span className="font-semibold text-pool-sofort">
                      niemand eingetragen – hier geht keine Erinnerung raus
                    </span>
                  )}
                  {a.bearbeiter && <span className="text-muted">von {a.bearbeiter}</span>}
                  <button
                    type="button"
                    disabled={erinnern.isPending || !a.abnehmerMail}
                    onClick={() => erinnern.mutate(a.id)}
                    title={
                      a.abnehmerMail
                        ? 'Schickt sofort eine Mail an den Abnehmer'
                        : 'Ohne Abnehmer mit Mailadresse gibt es niemanden zu erinnern'
                    }
                    className="ml-auto cursor-pointer rounded-lg border border-line2 bg-surface px-2.5 py-1 text-xs font-semibold text-ink hover:bg-[#f3f2ef] disabled:cursor-default disabled:opacity-50"
                  >
                    An Abnahme erinnern
                  </button>
                </div>

                {meldung?.id === a.id && (
                  <p
                    role="status"
                    className={`mt-1.5 text-[11.5px] ${meldung.gut ? 'text-good-text' : 'text-pool-sofort'}`}
                  >
                    {meldung.text}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardPad>
    </Card>
  )
}
